import React, { useEffect, useState, useCallback } from 'react';
import { users as usersApi, extractError } from '../services/api';
import { RoleBadge } from '../components/ui/StatusBadge';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/LoadingSpinner';
import { useToast } from '../store/ToastContext';
import { useAuth } from '../store/AuthContext';

const ROLES = ['administrator', 'fleet_manager', 'dispatcher', 'driver'];

const BLANK = { first_name: '', last_name: '', email: '', password: '', role: 'dispatcher', is_active: true };

function UserForm({ initial, onSave, onClose, isEdit }) {
  const toast = useToast();
  const [form, setForm] = useState({ ...BLANK, ...initial });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({...e,[k]:undefined})); };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim())  e.last_name  = 'Required';
    if (!isEdit && !form.email.trim()) e.email = 'Required';
    if (!isEdit && !form.password)     e.password = 'Minimum 8 characters';
    if (!form.role)                    e.role = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try { await onSave(form); }
    catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">First Name *</label>
          <input className={`input-field ${errors.first_name ? 'input-error' : ''}`} value={form.first_name} onChange={e => set('first_name', e.target.value)} />
          {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input className={`input-field ${errors.last_name ? 'input-error' : ''}`} value={form.last_name} onChange={e => set('last_name', e.target.value)} />
          {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
        </div>
      </div>
      <div>
        <label className="label">Email Address {!isEdit && '*'}</label>
        <input type="email" className={`input-field ${errors.email ? 'input-error' : ''}`} value={form.email} onChange={e => set('email', e.target.value)} disabled={isEdit} />
        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        {isEdit && <p className="text-xs text-gray-400 mt-1">Email cannot be changed after creation</p>}
      </div>
      <div>
        <label className="label">{isEdit ? 'New Password' : 'Password *'} {isEdit && <span className="text-gray-400 text-xs">(leave blank to keep)</span>}</label>
        <input type="password" className={`input-field ${errors.password ? 'input-error' : ''}`}
          value={form.password || ''} onChange={e => set('password', e.target.value)}
          placeholder={isEdit ? 'Leave blank to keep current' : 'Min 8 characters'} />
        {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Role *</label>
          <select className={`input-field ${errors.role ? 'input-error' : ''}`} value={form.role} onChange={e => set('role', e.target.value)}>
            {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}
          </select>
          {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
        </div>
        {isEdit && (
          <div>
            <label className="label">Account Status</label>
            <select className="input-field" value={form.is_active ? '1' : '0'} onChange={e => set('is_active', e.target.value === '1')}>
              <option value="1">Active</option>
              <option value="0">Deactivated</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
        </button>
      </div>
    </div>
  );
}

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const [userList, setList]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEdit]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await usersApi.list();
      setList(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const u = await usersApi.create(form);
    setList(prev => [u, ...prev]);
    setShowForm(false);
    toast.success(`User ${u.first_name} ${u.last_name} created`);
  };

  const handleUpdate = async (form) => {
    const u = await usersApi.update(editUser.id, form);
    setList(prev => prev.map(x => x.id === u.id ? u : x));
    setEdit(null);
    toast.success('User updated');
  };

  const handleDeactivate = async () => {
    setDeleting(true);
    try {
      await usersApi.delete(confirmDel.id);
      setList(prev => prev.map(u => u.id === confirmDel.id ? { ...u, is_active: 0 } : u));
      toast.success('User deactivated');
      setConfirmDel(null);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = userList.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return `${u.first_name} ${u.last_name}`.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New User
          </button>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="relative max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" className="input-field pl-9 py-2" placeholder="Search users…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="card overflow-hidden">
          {loading ? <TableSkeleton rows={6} cols={5} /> : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No users found</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">User</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Role</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Last Login</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(u => (
                  <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${!u.is_active ? 'opacity-60' : ''}`}>
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                        </div>
                        <span className="font-medium text-gray-900">{u.first_name} {u.last_name}</span>
                        {u.id === currentUser?.id && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">You</span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-gray-600">{u.email}</td>
                    <td className="table-cell"><RoleBadge role={u.role} /></td>
                    <td className="table-cell">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    </td>
                    <td className="table-cell text-gray-500 text-xs">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button onClick={() => setEdit(u)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium transition-colors">Edit</button>
                        {u.id !== currentUser?.id && u.is_active && (
                          <button onClick={() => setConfirmDel(u)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors">Deactivate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Create User" size="md">
        <UserForm initial={BLANK} onSave={handleCreate} onClose={() => setShowForm(false)} isEdit={false} />
      </Modal>

      {editUser && (
        <Modal isOpen onClose={() => setEdit(null)} title="Edit User" size="md">
          <UserForm initial={editUser} onSave={handleUpdate} onClose={() => setEdit(null)} isEdit />
        </Modal>
      )}

      <ConfirmDialog isOpen={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={handleDeactivate}
        title="Deactivate User" message={`Deactivate ${confirmDel?.first_name} ${confirmDel?.last_name}? They will no longer be able to sign in.`}
        confirmText="Deactivate" variant="danger" isLoading={deleting} />
    </div>
  );
}
